import { Box, Button, CardActions, CardHeader, Grid, ListItem, ListItemText, Menu, MenuItem, Stack, styled, Typography } from "@mui/material";
import { orange } from "@mui/material/colors";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { resetWorkspace } from "app/features/notification/notificationSlice";
import { useAppDispatch } from "app/store";
import { MprcRequestIndicator, MprcSpinnerIndicator, MprcText, MprcTypography } from "components";
import { IconButtonCog, IconButtonOverflowV } from "components/iconButtons";
import MprcAutoSizer from "components/MprcAutoSizer";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { NotificationType, NotificationWorkspaceDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps, VariableSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { MessengerResolvers } from "views/foundation/notification/resolvers";
import { MessengerResolver } from "views/foundation/notification/resolvers/MessengerResolver";
import { PriorityMessageResolver } from "views/foundation/notification/resolvers/PriorityMessageResolver";
import { NotificationConfigDialog } from "views/foundation/user/dialogs/NotificationConfigDialog";

/////////////////////////////////////////////////////////////////////////////////////////////////

const BoxHeader = styled(CardHeader)(
    ({ theme }) => ({
        "&.MuiCardHeader-root": {
            paddingLeft: 0,
            paddingRight: 13,
            paddingTop: 0,
            paddingBottom: 15
        },
        "&.MuiCardHeader-action": {
            marginTop: 0,
            marginRight: 0,
            alignSelf: "center"
        },
    })
);

const BoxItem = styled(
    Box,
    { shouldForwardProp: (propName: string) => propName != "isNew" }
)<{ isNew: boolean; }>(
    ({ theme, isNew }) => ({
        borderLeftColor: isNew ? orange[500] : "transparent",
        borderLeftStyle: "solid",
        borderLeftWidth: theme.spacing(0.5),
        paddingLeft: theme.spacing(0.5),
        marginBottom: theme.spacing(1),
    })
);

const BoxPriorityItem = styled(Box)(
    ({ theme }) => ({
        border: 2,
        borderColor: theme.palette.warning.main,
        borderStyle: "solid",
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
        marginRight: theme.spacing(0.5)
    })
);

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderMessageProps {
    notification: NotificationWorkspaceDto;
    onCloseMenu: () => void;
    hideNotificationMessage: (notification: string) => void;
    renderDialog: (dialog: any) => void;
}

const RenderMessage: React.FC<IRenderMessageProps> = ({ notification, onCloseMenu, hideNotificationMessage, renderDialog }) => {
    const [anchor, setAnchor] = useState<any>(null);
    const { t } = useTranslation("shared");

    var resolverType = MessengerResolvers.find(p => p.messengerId.includes(NotificationType[notification.notificationTypeId]) || p.messengerId.find(x => NotificationType[notification.notificationTypeId].match(x)));

    if (resolverType == null) {
        console.error("Cannot find Notification's Resolver", notification.notificationTypeId);
        resolverType = MessengerResolver;
    }
    const r = new resolverType(notification, onCloseMenu, renderDialog);

    ///
    const handleClickMenuItem = (action: string | null) => {
        switch (action) {
            case "hideNotification":
                hideNotificationMessage(notification.notificationId);
                setAnchor(null);
                break;
            default:
                setAnchor(null);
                break;
        }
    };

    return (
        <BoxItem
            alignItems="flex-start"
            isNew={notification.isNew}
        >
            <Stack direction="row" justifyContent="space-between" width="100%">
                <div>
                    <MprcTypography
                        variant="subtitle1"
                        component="div"
                        color="black"
                        noWrapRows={2}
                    >
                        {r.message}
                    </MprcTypography>

                    <Typography variant="caption">
                        {Formaters.relativeTime(notification.createdOn)}
                    </Typography>
                </div>
                <IconButtonOverflowV
                    size="small"
                    onClick={(e) => { setAnchor(e.currentTarget); }}
                />
            </Stack>

            <Menu
                open={anchor != null}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
            >
                <MenuItem key="hideNotification" onClick={() => handleClickMenuItem("hideNotification")}>
                    <MprcText color="error">{t("App.Notification.DeleteNotification")}</MprcText>
                </MenuItem>
            </Menu>
        </BoxItem>
    );
};

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderPriorityMessageProps {
    notification: NotificationWorkspaceDto;
    renderDialog: (dialog: any) => void;
}

const RenderPriorityMessage: React.FC<IRenderPriorityMessageProps> = ({ notification, renderDialog }) => {
    var r = new PriorityMessageResolver(renderDialog);
    return (
        <BoxPriorityItem>
            <Typography variant="subtitle1" color="black" component="div">
                <>{r.getMessage(notification)}</>
            </Typography>
        </BoxPriorityItem>
    );
};

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IWorkpsaceNotifications {
    onCloseMenu: () => void;
}

interface IDictionary {
    [id: string]: number;
}

export const WorkpsaceNotifications: React.FC<IWorkpsaceNotifications> = ({ onCloseMenu }) => {
    const { t } = useTranslation("shared");
    const session = useSession();
    const dispatch = useAppDispatch();
    const api = useCoreApi();

    const [loading, setLoading] = useState<boolean>(true);
    const [posting, setPosting] = useState<boolean>(false);
    const [items, setItems] = useState<NotificationWorkspaceDto[]>([]);
    const [dialog, setDialog] = useState<any>(null);
    const dropdownWrapperRef = useRef<HTMLDivElement>(null);

    const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const rowHeights = useRef<IDictionary>({});
    const endOfRecords = useRef<boolean>(true);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);
    const windowRef = useRef<HTMLElement | null>(null);
    const outerRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<VariableSizeList<NotificationWorkspaceDto> | null>(null);

    const minBatchSize = 15;

    const reset = () => {
        setItems([]);
        listRef.current?.resetAfterIndex(0);
        infiniteLoaderRef.current?.resetloadMoreItemsCache(true);
        rowHeights.current = {};
        loadData(0, 0);
    };

    ///
    const clearWorkspaceNotifications = async () => {
        try {
            setPosting(true);
            const response = await api.v1.notification.clearWorkspaceNotifications({ userId: session.user.userId });
            if (response.data) reset();
        }
        catch (e) {
            MprcRequestSnackbarErrorReport(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const hideNotificationMessage = async (notificationId: string) => {
        try {
            setPosting(true);
            const response = await api.v1.notification.hideWorkspaceNotification({ notificationId, userId: session.user.userId });
            if (response.data) reset();
        }
        catch (e) {
            MprcRequestSnackbarErrorReport(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const loadData = async (startIndex: number, endIndex: number) => {
        setLoading(true);
        try {
            var response = await api.v1.notification.getWorkspaceMessages({ userId: session.user.userId, startIndex, endIndex: endIndex + minBatchSize });

            // Há mais registros?
            const regularMessages = response.data.filter(p => p.regularNotification != null);
            endOfRecords.current = regularMessages.length < (minBatchSize + endIndex - startIndex + 1);


            if (startIndex == 0) {
                // Inicializa o array de itens.
                setItems(response.data);
            }
            else {
                // Adiciona mais itens no array.
                setItems([...items, ...response.data]);
            }
        }
        catch (e) {
            if (!isCanceledError(e)) {
                endOfRecords.current = true;
            }
        }
        finally {
            setLoading(false);
        }
    };


    //
    function getRowHeight(index: number) {
        return rowHeights.current[index] || 74;
    }

    function setRowHeight(index: number, size: number) {
        if (rowHeights.current[index] == undefined || rowHeights.current[index] != size) {
            listRef.current?.resetAfterIndex(index);
            rowHeights.current = { ...rowHeights.current, [index]: size };
        }
    }

    //
    const handleWindowScroll = () => {
        listRef.current?.scrollTo(windowRef.current!.offsetTop);
    };

    /** Calcula a área ocupada ACIMA do Infinite Scroll */
    useEffect(() => {
        windowRef.current = document.getElementById("OuterNotificationArea");
        windowRef.current?.addEventListener('scroll', handleWindowScroll);

        return () => {
            windowRef.current?.removeEventListener('scroll', handleWindowScroll);
            if (timeout.current) clearTimeout(timeout.current);
        };
    }, []);

    ///
    const loadNextPage = (startIndex: number, stopIndex: number) => {
        return new Promise<void>((resolve) => {
            loadData(startIndex, stopIndex).then(() => resolve());
        });
    };

    // Se há mais itens para ser carregado, adicionamos UMA LINHA para que o Indicador de Carregamento apareça.
    const itemCount = !endOfRecords.current ? items.length + 1 : items.length;

    // Permite apenas uma página de items pode vez.
    const loadMoreItems = loading ? () => { } : loadNextPage;

    // Verifica se a linha está carregada, exceto o Indicador de Carregamento.
    const isItemLoaded = (index: number) => {
        return endOfRecords.current || index < items.length;
    };

    function RenderRow(props: ListChildComponentProps<NotificationWorkspaceDto>) {
        const rowRef = useRef<HTMLDivElement | null>(null);
        const { index, style } = props;

        React.useEffect(() => {
            if (rowRef.current) {
                setRowHeight(index, rowRef.current.clientHeight);
            }
        }, [rowRef]);

        if (index > items.length - 1) {
            return (
                <Box style={style} sx={{ mt: 1 }}>
                    <MprcSpinnerIndicator />
                </Box>
            );
        }

        const data = items[index];

        return <Box id={`item-${index}`} key={index} style={style}>
            <Box ref={rowRef}>
                <Box sx={{ pb: .5 }}>
                    {
                        data.priorityNotification != null
                            ? <RenderPriorityMessage
                                notification={data}
                                renderDialog={(dialog: any) => {
                                    setDialog(dialog);
                                }}
                            />
                            : <RenderMessage
                                notification={data}
                                onCloseMenu={onCloseMenu}
                                hideNotificationMessage={hideNotificationMessage}
                                renderDialog={(dialog: any) => {
                                    setDialog(dialog);
                                }}
                            />
                    }
                </Box>
            </Box>
        </Box>;
    }

    ///
    const renderMessages = () => {
        return loading && (items ?? []).length == 0
            ? <MprcSpinnerIndicator />
            : !loading && (items ?? []).length == 0
                ? <ListItem disableGutters><ListItemText primary={t("App.Notification.EmptyNotificationList")} primaryTypographyProps={{ variant: "subtitle1", color: "black" }} /></ListItem>
                : <MprcAutoSizer>
                    {
                        ({ width, height }) =>

                            <InfiniteLoader
                                isItemLoaded={isItemLoaded}
                                itemCount={itemCount}
                                loadMoreItems={loadMoreItems}
                                ref={infiniteLoaderRef}
                                minimumBatchSize={minBatchSize}
                            >
                                {({ onItemsRendered, ref }) => (
                                    <VariableSizeList
                                        layout="vertical"
                                        style={{ overflow: "visible", margin: "0 auto", height: "100%" }}
                                        height={window.innerHeight}
                                        width={420}
                                        useIsScrolling
                                        itemCount={itemCount}
                                        outerRef={outerRef}
                                        itemSize={getRowHeight}
                                        onItemsRendered={onItemsRendered}
                                        ref={list => {
                                            // Pass List ref through to InfiniteLoader
                                            ref(list);
                                            // And store a copy for yourself.
                                            listRef.current = list;
                                        }}
                                    >
                                        {RenderRow}
                                    </VariableSizeList>
                                )}
                            </InfiniteLoader>
                    }
                </MprcAutoSizer>;
    };

    ///
    useEffect(() => {
        loadData(0, 0);
        return () => { dispatch(resetWorkspace()); };
    }, []);

    ///
    return (
        <Box
            id="notificationWorkspace"
            sx={{ width: 450, height: "calc(100vh - 100px)", position: "relative" }}
            ref={dropdownWrapperRef}
        >
            <MprcRequestIndicator type="post" visible={posting} />

            <Grid
                container
                direction="column"
                wrap="nowrap"
                sx={
                    (theme) => (
                        {
                            padding: theme.spacing(),
                            height: "100%",
                            overflow: "hidden",
                            position: "relative"
                        })
                }>
                <Grid
                    sx={
                        (theme) => (
                            {
                                backgroundColor: theme.palette.common.white,
                                marginBottom: theme.spacing(),
                                paddingRight: 0
                            })
                    }
                >
                    <BoxHeader
                        action={
                            <IconButtonCog
                                color="muted"
                                onClick={() => setDialog(<NotificationConfigDialog onClose={() => setDialog(null)} />)}
                            />
                        }
                        title={t("App.Notification.Title")}
                    />
                </Grid>

                <Grid
                    id="OuterNotificationArea"
                    size="grow"
                    width="100%"
                    sx={
                        {
                            overflowX: "hidden",
                            overflowy: "auto",
                            mt: 1
                        }
                    }
                    className={AppGlobalClassKeys.thinScrollbar}
                >
                    {renderMessages()}
                </Grid>

                <Grid width="100%" alignSelf="center">
                    {
                        items && items.length > 0 &&
                        <CardActions sx={{ justifyContent: "center" }}>
                            <Button variant="contained" onClick={clearWorkspaceNotifications}>{t("App.Notification.MarkRead")}</Button>
                        </CardActions>
                    }
                </Grid>
            </Grid>
            {dialog}
        </Box>
    );
};
